import { CartItemsV3Query } from '~/__generated__/app/query/Cart/Cart.graphql'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { ItemType } from '~/api/routes/domestic/order/controller/type'
import { MaxCouponDiscountPropertyRequiredParams } from '~/api/converters/domestic/order/Discount'
import { MaxCouponDiscountCouponPlatformRequiredParams } from '~/api/converters/domestic/order/Discount/CouponSelectModal/couponPlatform'
import { TDomesticCoupon } from '~/api/services/coupon/index.type'
import { ICouponInstances } from '~/api/services/coupon-platform/index.type'
import { getDomesticCouponMaxDiscount } from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getDomesticCouponMaxDiscount'
import { getCouponPlatformMaxDiscount } from '~/api/converters/domestic/order/Coupons/MaxDiscountCoupons/getCouponPlatformMaxDiscount'
import {
  getTrainPackageDiscountAmount,
  TGetTrainPackageDiscountAmountParams,
} from '~/api/utils/train'
import { isTruthy } from '~/app/utils'
import { MaxCouponDiscount } from '~/api/converters/domestic/order/Discount/index.type'
import * as Sentry from '@sentry/browser'

interface ICouponMaxDiscountCache {
  [ItemType.Property]: Record<string, MaxCouponDiscount>
  [ItemType.Leisure]: Record<string, MaxCouponDiscount>
  [ItemType.Train]: Record<string, MaxCouponDiscount>
  [ItemType.Bus]: Record<string, MaxCouponDiscount>
}

interface ICacheId {
  cacheId: string
}

export default function usePaymentInfoHooks({
  cartItems,
  maxDiscountLimit,
  isFirstAccessLoginUser,
}: {
  cartItems: CartItemsV3Query['cartItemsV3']
  maxDiscountLimit: { timeout: number; maxLength: number }
  isFirstAccessLoginUser: boolean
}) {
  const couponMaxDiscountCache = useRef<ICouponMaxDiscountCache>({
    property: {},
    leisure: {},
    train: {},
    bus: {},
  })

  // 선택된 아이템들의 캐시 키를 반환
  const getKeyOfSelectedItems = useCallback((selectedItems: ICacheId[]) => {
    return selectedItems
      .map(item => {
        return item.cacheId
      })
      .join(':')
  }, [])

  // 최대할인금액 계산 함수 인자 인터페이스에 맞춰 cartItems 를 변환
  const { properties, trains, buses, leisures } = useMemo(() => {
    let [pcnt, lcnt, tcnt, bcnt] = [0, 0, 0, 0]
    return cartItems.reduce<{
      properties: (MaxCouponDiscountPropertyRequiredParams & ICacheId)[]
      leisures: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
      trains: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
      buses: (MaxCouponDiscountCouponPlatformRequiredParams & ICacheId)[]
    }>(
      (acc, cur) => {
        if (cur.__typename === 'DomesticAccommodationCartItem') {
          acc.properties = acc.properties.concat(
            cur.options
              .filter(option => option.isChosen && option.availability)
              .map(option => ({
                cacheId: option.id,
                price: option.price,
                type: ItemType.Property,
                typeIdx: pcnt++,
                useType: (option.useType === 'DAY_USE'
                  ? 'R'
                  : option.useType === 'OVER_NIGHT'
                    ? 'S'
                    : 'D') as 'D' | 'R' | 'S',
                id: cur.propertyId,
                checkInDate: option.checkInDate,
                coupons: option.roomType.coupons as TDomesticCoupon[],
                additionalOneCoupons: option.roomType.additionalOneCoupons as TDomesticCoupon[],
                additionalTwoCoupons: option.roomType.additionalTwoCoupons as TDomesticCoupon[],
              }))
          )
          return acc
        }
        if (cur.__typename === 'DomesticLeisureCartItem') {
          acc.leisures = acc.leisures.concat(
            cur.options
              .filter(option => option.isChosen && option.availability)
              .map(option => {
                return {
                  cacheId: option.id,
                  id: +option.id,
                  price: option.price * (option?.quantity || 1),
                  type: ItemType.Leisure as ItemType.Leisure,
                  typeIdx: lcnt++,
                  coupons: option.leisureCoupons as ICouponInstances[],
                }
              })
          )
          return acc
        }
        if (cur.__typename === 'DomesticTrainCartItem') {
          acc.trains = acc.trains.concat(
            cur.options
              .filter(option => option.isChosen && option.availability)
              .map(option => {
                return {
                  cacheId: option.id,
                  price: option.price,
                  type: ItemType.Train as ItemType.Train,
                  typeIdx: tcnt++,
                  coupons: option.coupons as ICouponInstances[],
                }
              })
          )
          return acc
        }
        if (cur.__typename === 'DomesticBusCartItem') {
          acc.buses = acc.buses.concat(
            cur.options
              .filter(option => option.isChosen && option.availability)
              .map(option => {
                return {
                  cacheId: option.id,
                  price: option.price,
                  type: ItemType.Bus as ItemType.Bus,
                  typeIdx: bcnt++,
                  coupons: option.coupons as ICouponInstances[],
                }
              })
          )
          return acc
        }
        return acc
      },
      { properties: [], leisures: [], trains: [], buses: [] }
    )
  }, [cartItems])

  const isMaxLengthExceeded = useMemo(() => {
    return (
      properties.length + leisures.length + trains.length + buses.length >
      maxDiscountLimit.maxLength
    )
  }, [cartItems, maxDiscountLimit])

  // 묶음할인 계산 함수 인자 인터페이스에 맞춰 cartItems 를 변환
  const convertedCartItemsForTrainPackageDiscount =
    useMemo<TGetTrainPackageDiscountAmountParams>(() => {
      return cartItems
        .map(cartItem => {
          if (cartItem.__typename === 'DomesticTrainCartItem') {
            return cartItem.options
              .map(option => {
                return option.trains
                  .filter(train => !!train.price && option.availability && option.isChosen)
                  .map(train => ({
                    price: train.price,
                    packageDiscount: train.packageDiscount || 0,
                  }))
              })
              .flat()
          }
          if (cartItem.__typename === 'DomesticAccommodationCartItem') {
            return cartItem.options
              .filter(property => !!property.price && property.availability && property.isChosen)
              .map(property => {
                return {
                  price: property.price,
                  packageDiscount: 0,
                }
              })
          }
          return
        })
        .flat()
        .filter(isTruthy)
    }, [cartItems])

  // 선택된 cartItems 의 캐시키와 최대할인금액 정보를 반환
  const getSelectedKeyAndMaxDiscount = useCallback(
    ({
      items,
      getMaxDiscount,
      itemType,
    }: {
      itemType: ItemType
      items: ICacheId[]
      getMaxDiscount: (params, maxDiscountLimit) => MaxCouponDiscount
    }) => {
      const selectedKey = getKeyOfSelectedItems(items)
      if (isMaxLengthExceeded) {
        return {
          selectedKey,
          maxDiscount: {
            maxDiscount: 0,
            discountAppliedItems: [],
          },
        }
      }

      const maxDiscount =
        couponMaxDiscountCache.current[itemType][selectedKey] ||
        getMaxDiscount(items, maxDiscountLimit)
      return { selectedKey, maxDiscount }
    },
    [isMaxLengthExceeded]
  )

  const {
    propertyCouponMaxDiscount,
    leisureCouponMaxDiscount,
    trainCouponMaxDiscount,
    busCouponMaxDiscount,
  } = useMemo(() => {
    const propertyCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: properties,
      getMaxDiscount: getDomesticCouponMaxDiscount,
      itemType: ItemType.Property,
    })
    const leisureCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: leisures,
      itemType: ItemType.Leisure,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })
    const trainCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: trains,
      itemType: ItemType.Train,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })
    const busCouponMaxDiscount = getSelectedKeyAndMaxDiscount({
      items: buses,
      itemType: ItemType.Bus,
      getMaxDiscount: getCouponPlatformMaxDiscount,
    })

    return {
      propertyCouponMaxDiscount,
      leisureCouponMaxDiscount,
      trainCouponMaxDiscount,
      busCouponMaxDiscount,
    }
  }, [cartItems])

  const trainPackageDiscountAmount = useMemo(
    () => getTrainPackageDiscountAmount(convertedCartItemsForTrainPackageDiscount),
    [cartItems]
  )

  // 선택된 cartItems 의 최대할인 금액과 타입을 반환
  const { maxDiscountAmount, maxDiscountTypeName, isTimeout } = useMemo(() => {
    if (
      propertyCouponMaxDiscount.maxDiscount.isTimeout ||
      leisureCouponMaxDiscount.maxDiscount.isTimeout ||
      trainCouponMaxDiscount.maxDiscount.isTimeout ||
      busCouponMaxDiscount.maxDiscount.isTimeout
    ) {
      return {
        maxDiscountAmount: 0,
        maxDiscountTypeName: '',
        isTimeout: true,
      }
    }

    const couponMaxDiscountAmount =
      propertyCouponMaxDiscount.maxDiscount.maxDiscount +
      trainCouponMaxDiscount.maxDiscount.maxDiscount +
      busCouponMaxDiscount.maxDiscount.maxDiscount +
      leisureCouponMaxDiscount.maxDiscount.maxDiscount

    const maxDiscountAmount = Math.max(couponMaxDiscountAmount, trainPackageDiscountAmount)

    return {
      maxDiscountAmount,
      maxDiscountTypeName:
        couponMaxDiscountAmount > trainPackageDiscountAmount ? '쿠폰 할인' : '묶음 할인',
      isTimeout: false,
    }
  }, [cartItems])

  // 체크 박스 토글 등 cartItems 가 변경될 때마다 부하가 큰 최대할인함수 실행을 최소화하기 위해 최대할인금액 정보 캐시 업데이트
  useEffect(() => {
    const { isTimeout: isPropertyTimeout } = propertyCouponMaxDiscount.maxDiscount
    const { isTimeout: isLeisureTimeout } = leisureCouponMaxDiscount.maxDiscount
    const { isTimeout: isTrainTimeout } = trainCouponMaxDiscount.maxDiscount
    const { isTimeout: isBusTimeout } = busCouponMaxDiscount.maxDiscount

    if (isPropertyTimeout || isLeisureTimeout || isTrainTimeout || isBusTimeout) {
      Sentry.withScope(scope => {
        const setMashupDiscountInfo = (
          label: string,
          mashup: { coupons: any[]; additionalOneCoupons?: any[]; additionalTwoCoupons?: any[] }[]
        ) => {
          scope.setExtra(
            label,
            mashup.map(p => ({
              couponLength: p.coupons.length,
              additionalOneCouponLength: p.additionalOneCoupons?.length,
              additionalTwoCouponLength: p.additionalTwoCoupons?.length,
            }))
          )
        }
        setMashupDiscountInfo('propertyMashup 할인 내용', properties)
        setMashupDiscountInfo('leisureMashup 할인 내용', leisures)
        setMashupDiscountInfo('trainMashup 할인 내용', trains)
        setMashupDiscountInfo('busMashup 할인 내용', buses)
        Sentry.captureMessage('장바구니 > 쿠폰 최대 할인 시간 초과')
      })
    }

    couponMaxDiscountCache.current = {
      [ItemType.Property]: {
        ...couponMaxDiscountCache.current[ItemType.Property],
        [propertyCouponMaxDiscount.selectedKey]: propertyCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Leisure]: {
        ...couponMaxDiscountCache.current[ItemType.Leisure],
        [leisureCouponMaxDiscount.selectedKey]: leisureCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Train]: {
        ...couponMaxDiscountCache.current[ItemType.Train],
        [trainCouponMaxDiscount.selectedKey]: trainCouponMaxDiscount.maxDiscount,
      },
      [ItemType.Bus]: {
        ...couponMaxDiscountCache.current[ItemType.Bus],
        [busCouponMaxDiscount.selectedKey]: busCouponMaxDiscount.maxDiscount,
      },
    }
  }, [cartItems])

  if (!isFirstAccessLoginUser) {
    return {
      maxDiscountAmount: 0,
      maxDiscountTypeName: '',
      isTimeout: false,
      isMaxLengthExceeded: false,
    }
  }

  return {
    maxDiscountAmount,
    maxDiscountTypeName,
    isTimeout,
    isMaxLengthExceeded,
  }
}
